import React, { useState, useEffect, useCallback } from 'react';
import { Database, Plus, Trash2, RefreshCw, Download, Upload, ChevronRight, ChevronDown, File, Folder, Loader2, Copy, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function CollectionItem({ col, projectId, onSelect, selected, onDelete }) {
  return (
    <div 
      onClick={() => onSelect(col)}
      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer ${selected?.name === col.name ? 'bg-emerald-500/20 text-emerald-400' : 'hover:bg-slate-700/50 text-gray-300'}`}
    >
      <div className="flex items-center gap-2">
        <Folder className="w-4 h-4" />
        <span className="text-sm">{col.name}</span>
        <span className="text-xs text-gray-500">({col.document_count || 0})</span>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onDelete(col.name); }} className="p-1 text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function DocumentRow({ doc, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const dataStr = JSON.stringify(doc.data, null, 2);
  const preview = dataStr.length > 100 ? dataStr.slice(0, 100) + '...' : dataStr;
  
  return (
    <div className="border border-slate-700 rounded-lg mb-2 overflow-hidden">
      <div className="flex items-center justify-between p-3 bg-slate-800/50 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <File className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-white font-mono">{doc.id.slice(0, 8)}...</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onEdit(doc); }} className="px-2 py-1 text-xs text-cyan-400 hover:bg-cyan-500/20 rounded">Editar</button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(doc.id); }} className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/20 rounded">Eliminar</button>
        </div>
      </div>
      {expanded && (
        <pre className="p-3 bg-black text-xs text-gray-300 overflow-x-auto">{dataStr}</pre>
      )}
      {!expanded && (
        <div className="px-3 py-2 text-xs text-gray-500 font-mono truncate">{preview}</div>
      )}
    </div>
  );
}

function DatabasePanel({ projectId, onClose }) {
  const [dbInfo, setDbInfo] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedCol, setSelectedCol] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [showNewDoc, setShowNewDoc] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newDocData, setNewDocData] = useState('{\n  \n}');
  const [editingDoc, setEditingDoc] = useState(null);
  const [importData, setImportData] = useState('');
  const [connCopied, setConnCopied] = useState(false);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const token = localStorage.getItem('cloudtexa_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(`${API}/api/database/${projectId}`, { headers });
      setDbInfo(res.data);
      if (res.data.provisioned) {
        const colRes = await axios.get(`${API}/api/database/${projectId}/collections`, { headers });
        setCollections(colRes.data.collections || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const provisionDb = async () => {
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.post(`${API}/api/database/${projectId}/provision`, { name: 'project_db', size_mb: 512 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Base de datos creada');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const fetchDocuments = async (col) => {
    setSelectedCol(col);
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.get(`${API}/api/database/${projectId}/collections/${col.name}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      setDocuments(res.data.documents || []);
    } catch (e) { toast.error('Error cargando documentos'); }
    setLoadingDocs(false);
  };

  const createCollection = async () => {
    if (!newColName.trim()) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.post(`${API}/api/database/${projectId}/collections`, { name: newColName }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Colección creada');
      setNewColName('');
      setShowNewCollection(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteCollection = async (name) => {
    if (!window.confirm(`¿Eliminar colección "${name}" y todos sus documentos?`)) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.delete(`${API}/api/database/${projectId}/collections/${name}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Colección eliminada');
      if (selectedCol?.name === name) { setSelectedCol(null); setDocuments([]); }
      fetchData();
    } catch (e) { toast.error('Error'); }
  };

  const saveDocument = async () => {
    try {
      const data = JSON.parse(editingDoc ? editingDoc.newData : newDocData);
      const token = localStorage.getItem('cloudtexa_token');
      if (editingDoc) {
        await axios.put(`${API}/api/database/${projectId}/collections/${selectedCol.name}/documents/${editingDoc.id}`, { data }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Documento actualizado');
      } else {
        await axios.post(`${API}/api/database/${projectId}/collections/${selectedCol.name}/documents`, { data }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Documento creado');
      }
      setShowNewDoc(false);
      setEditingDoc(null);
      setNewDocData('{\n  \n}');
      fetchDocuments(selectedCol);
    } catch (e) { 
      if (e instanceof SyntaxError) toast.error('JSON inválido');
      else toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('¿Eliminar documento?')) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.delete(`${API}/api/database/${projectId}/collections/${selectedCol.name}/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Eliminado');
      fetchDocuments(selectedCol);
    } catch (e) { toast.error('Error'); }
  };

  const exportData = async () => {
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.post(`${API}/api/database/${projectId}/export`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `db_export_${Date.now()}.json`;
      a.click();
      toast.success('Exportado');
    } catch (e) { toast.error('Error exportando'); }
  };

  const importDataFn = async () => {
    try {
      const data = JSON.parse(importData);
      const token = localStorage.getItem('cloudtexa_token');
      for (const [colName, docs] of Object.entries(data.collections || {})) {
        await axios.post(`${API}/api/database/${projectId}/import`, { collection: colName, documents: docs, replace_existing: false }, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Datos importados');
      setShowImport(false);
      setImportData('');
      fetchData();
    } catch (e) {
      if (e instanceof SyntaxError) toast.error('JSON inválido');
      else toast.error('Error importando');
    }
  };

  const copyConnection = async () => {
    try {
      const token = localStorage.getItem('cloudtexa_token');
      const res = await axios.get(`${API}/api/database/${projectId}/connection-string`, { headers: { Authorization: `Bearer ${token}` } });
      navigator.clipboard.writeText(res.data.connection_string);
      setConnCopied(true);
      setTimeout(() => setConnCopied(false), 2000);
      toast.success('Copiado al portapapeles');
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;

  return (
    <div className="h-full flex flex-col bg-slate-900" data-testid="database-panel">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-emerald-400" />
            <h2 className="font-medium text-white">Base de Datos</h2>
            <span className="px-2 py-0.5 text-xs rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">MongoDB</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
      </div>

      {!dbInfo?.provisioned ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <Database className="w-16 h-16 text-emerald-400 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-white mb-2">Configura tu Base de Datos</h3>
            <p className="text-sm text-gray-400 mb-6">Crea una base de datos MongoDB para almacenar los datos de tu aplicación.</p>
            <button onClick={provisionDb} className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium hover:opacity-90">
              Crear Base de Datos
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar - Collections */}
          <div className="w-56 border-r border-slate-700 flex flex-col">
            <div className="p-3 border-b border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-400 uppercase">Colecciones</span>
                <button onClick={() => setShowNewCollection(true)} className="p-1 text-emerald-400 hover:bg-emerald-500/20 rounded"><Plus className="w-4 h-4" /></button>
              </div>
              {showNewCollection && (
                <div className="flex gap-1">
                  <input type="text" value={newColName} onChange={e => setNewColName(e.target.value)} placeholder="nombre" className="flex-1 px-2 py-1 text-sm rounded bg-slate-700 text-white outline-none" autoFocus />
                  <button onClick={createCollection} className="px-2 py-1 text-xs bg-emerald-500 text-white rounded">OK</button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {collections.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-4">Sin colecciones</p>
              ) : (
                collections.map(col => <CollectionItem key={col.name} col={col} projectId={projectId} onSelect={fetchDocuments} selected={selectedCol} onDelete={deleteCollection} />)
              )}
            </div>
            <div className="p-2 border-t border-slate-700 space-y-1">
              <button onClick={exportData} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:bg-slate-700/50 rounded"><Download className="w-3 h-3" />Exportar</button>
              <button onClick={() => setShowImport(true)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:bg-slate-700/50 rounded"><Upload className="w-3 h-3" />Importar</button>
              <button onClick={copyConnection} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:bg-slate-700/50 rounded">{connCopied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}Conexión</button>
            </div>
          </div>

          {/* Main - Documents */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedCol ? (
              <>
                <div className="p-3 border-b border-slate-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-emerald-400" />
                    <span className="font-medium text-white">{selectedCol.name}</span>
                    <span className="text-xs text-gray-500">({documents.length} docs)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => fetchDocuments(selectedCol)} className="p-1.5 text-gray-400 hover:bg-slate-700 rounded"><RefreshCw className="w-4 h-4" /></button>
                    <button onClick={() => { setShowNewDoc(true); setEditingDoc(null); setNewDocData('{\n  \n}'); }} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30"><Plus className="w-4 h-4" />Nuevo</button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {loadingDocs ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-400" /></div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <File className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Sin documentos</p>
                    </div>
                  ) : (
                    documents.map(doc => <DocumentRow key={doc.id} doc={doc} onEdit={(d) => { setEditingDoc({ ...d, newData: JSON.stringify(d.data, null, 2) }); setShowNewDoc(true); }} onDelete={deleteDocument} />)
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Database className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Selecciona una colección</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New/Edit Document Modal */}
      {showNewDoc && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-4 w-full max-w-lg mx-4">
            <h3 className="font-medium text-white mb-3">{editingDoc ? 'Editar Documento' : 'Nuevo Documento'}</h3>
            <textarea value={editingDoc ? editingDoc.newData : newDocData} onChange={e => editingDoc ? setEditingDoc({ ...editingDoc, newData: e.target.value }) : setNewDocData(e.target.value)} className="w-full h-64 px-3 py-2 rounded-lg bg-slate-900 text-white font-mono text-sm outline-none resize-none" placeholder='{"key": "value"}' />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setShowNewDoc(false); setEditingDoc(null); }} className="px-4 py-2 text-sm text-gray-400 hover:bg-slate-700 rounded-lg">Cancelar</button>
              <button onClick={saveDocument} className="px-4 py-2 text-sm bg-emerald-500 text-white rounded-lg">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-4 w-full max-w-lg mx-4">
            <h3 className="font-medium text-white mb-3">Importar Datos</h3>
            <p className="text-xs text-gray-400 mb-2">Pega el JSON exportado previamente</p>
            <textarea value={importData} onChange={e => setImportData(e.target.value)} className="w-full h-64 px-3 py-2 rounded-lg bg-slate-900 text-white font-mono text-sm outline-none resize-none" placeholder='{"collections": {...}}' />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setShowImport(false); setImportData(''); }} className="px-4 py-2 text-sm text-gray-400 hover:bg-slate-700 rounded-lg">Cancelar</button>
              <button onClick={importDataFn} className="px-4 py-2 text-sm bg-emerald-500 text-white rounded-lg">Importar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatabasePanel;
